import React, { useMemo, useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getOEMChart } from '@/services/api/dashboard';

interface OEMData {
    name: string;
    value: number;
    color: string;
    percentage: string;
    id: string;
}

interface OEMApiResponse {
    oemID: string;
    oemName: string;
    totalVehicle: number;
    percentageOfTotal: number;
}

interface OEMsDistributionChartProps {
    onSeeAll?: () => void;
}

const CHART_COLORS = [
    '#FBBF24',
    '#8B5CF6',
    '#3B82F6',
    '#14B8A6',
    '#6366F1',
    '#EC4899',
    '#10B981',
    '#F97316',
    '#06B6D4'
];

const OEMsDistributionChart: React.FC<OEMsDistributionChartProps> = ({ onSeeAll }) => {
    const [apiData, setApiData] = useState<OEMApiResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const initialized = React.useRef(false);

    // Fetch data from API on mount
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const fetchChartData = async () => {
            try {
                setLoading(true);
                const response = await getOEMChart();
                setApiData(response.data || []);
            } catch (error) {
                console.error('Error fetching OEM chart data:', error);
                setApiData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    // Map API data to display format - take top 5
    const chartData: OEMData[] = useMemo(() => {
        return apiData.slice(0, 5).map((item, index) => ({
            name: item.oemName || 'Unknown',
            value: Number(item.totalVehicle) || 0,
            color: CHART_COLORS[index % CHART_COLORS.length],
            percentage: (Number(item.percentageOfTotal) || 0).toFixed(0) + '%',
            id: item.oemID
        }));
    }, [apiData]);

    const chartOptions: ApexOptions = useMemo(() => ({
        chart: {
            type: 'donut',
            height: 280,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
        },
        plotOptions: {
            pie: {
                startAngle: 0,
                endAngle: 360,
                expandOnClick: false,
                offsetX: 0,
                offsetY: 0,
                customScale: 1,
                dataLabels: {
                    offset: 0,
                    minAngleToShowLabel: 10
                },
                donut: {
                    size: '75%', // Inner hole size
                    background: 'transparent',
                    labels: {
                        show: false,
                    }
                }
            }
        },
        colors: chartData.map(item => item.color),
        labels: chartData.map(item => item.name),
        dataLabels: {
            enabled: false,
        },
        legend: {
            show: false, // Custom legend dibuat manual di bawah
        },
        stroke: {
            show: true,
            width: 4,
            colors: ['#FFFFFF'], // White gap between segments
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: function (value) {
                    return value.toLocaleString();
                }
            },
            style: {
                fontSize: '14px',
                fontFamily: 'inherit',
            }
        },
        states: {
            hover: {
                filter: {
                    type: 'lighten',
                    value: 0.1,
                }
            },
            active: {
                filter: {
                    type: 'none',
                }
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                chart: {
                    height: 240
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%'
                        }
                    }
                }
            }
        }]
    }), [chartData]);

    const series = useMemo(() =>
        chartData.map(item => item.value),
        [chartData]
    );

    return (
        <div className="bg-white rounded-2xl border border-gray-200 px-6 pt-5 pb-6 shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-file-chart-line text-lg text-neutral-900" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900" style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}>
                        OEMs Distribution
                    </h3>
                </div>

                <button
                    onClick={onSeeAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    See All
                </button>
            </div>

            {/* Chart */}
            <div className="flex justify-center mb-2">
                <div style={{ width: '100%', maxWidth: '280px', aspectRatio: '1/1' }} className="flex items-center justify-center">
                    {loading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    ) : (
                        <Chart
                            options={chartOptions}
                            series={series}
                            type="donut"
                            height="100%"
                        />
                    )}
                </div>
            </div>

            {/* Custom Legend */}
            <div className={`space-y-3 ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
                {chartData.map((item) => (
                    <div key={item.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                    backgroundColor: item.color,
                                    border: '2px solid white',
                                    boxShadow: '0px 2px 4px 0px #1B1C1D0A'
                                }}
                            />
                            <span
                                className="font-medium"
                                style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.006em',
                                    color: '#5C5C5C'
                                }}
                            >
                                {item.name}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span
                                className="font-medium"
                                style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.006em',
                                    color: '#5C5C5C'
                                }}
                            >
                                {item.value.toLocaleString()}
                            </span>
                            <span style={{ fontSize: '14px', color: '#D1D1D1' }}>•</span>
                            <span
                                className="font-normal w-8 text-left"
                                style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.006em',
                                    color: '#A3A3A3'
                                }}
                            >
                                {item.percentage}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OEMsDistributionChart;
